import React, { createContext } from 'react'
import { BaseContextState, BaseContextProvider } from '../BaseContext'
import IMappingColor from '../../interfaces/IMappingColor'
import { SearchRequest, MappingColorSaveRequest } from '../../services/api/interfaces/ApiRequest'
import { mappingColorList, mappingColorSave } from '../../services/api'

interface MappingColorContextState extends BaseContextState {
  mappingColors: Array<IMappingColor> | null,
  mappingColor: IMappingColor | null
}

interface MappingColorContextData extends MappingColorContextState {
  resetErrors: Function,
  requestMappingColorList: (args: SearchRequest) => Promise<any>,
  requestMappingColorFind: (args: SearchRequest) => Promise<any>,
  requestMappingColorSave: (args: MappingColorSaveRequest) => void,
}

// Create context
const { Provider, Consumer } = createContext({} as MappingColorContextData)

// Export consumer
export const MappingColorConsumer = Consumer

export class MappingColorProvider extends BaseContextProvider {
  state: MappingColorContextState = {
    mappingColors: null,
    mappingColor: null
  }

  /**
   * Make api request for mapping color list
   * 
   * @param {SearchRequest} payload 
   */
  requestMappingColorList = (payload: SearchRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        const response = await mappingColorList(payload)
  
        // Process our response
        this.processResponse(response, { 
          paginate: {
            destination: 'mappingColors'
          },
        })

        if (this.state.success) {
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for mapping color finder
   * 
   * @param {SearchRequest} payload 
   */
  requestMappingColorFind = (payload: SearchRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null,
        mappingColor: null
      }, async () => {
        payload.search = {
          ...payload.search,
          shelter: {
            with: {
              _fields: ["id", "uuid", "name"]
            }
          },
        }
        payload.options = {
          ...payload.options,
          _single: true
        }
  
        const response = await mappingColorList(payload)
  
        // Process our response
        this.processResponse(response, { 
          mappings: {
            destination: 'mappingColor'
          },
        })

        if (this.state.success) {
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for mapping color save
   * 
   * @param {MappingColorSaveRequest} payload 
   */
  requestMappingColorSave = (payload: MappingColorSaveRequest) => {
    // No need to null `mappingColor` on save: the form stays mounted (its render
    // no longer depends on this value) and re-fetches fresh whenever the modal reopens.
    return new Promise<void>((resolve, reject) => this.setState({
      fetching: true,
      error: null,
      // mappingColor: null
    }, async () => {
      try {
        const response = await mappingColorSave(payload)
        
        // Process our response
        const result = await this.processResponse(response, {
          mappings: [
            { origin: 'mapping_color', destination: 'mappingColor' }
          ]
        })

        // throw if error happend
        if (result.error) {
          return reject(new Error(result.error))
        }
      } finally {
        resolve()
      }
    }))
  }

  render() {
    return (
      <Provider
        value={{
          // States
          ...this.state,

          // Dispatches
          resetErrors: this.resetErrors,
          requestMappingColorList: this.requestMappingColorList,
          requestMappingColorFind: this.requestMappingColorFind,
          requestMappingColorSave: this.requestMappingColorSave
        }}
      >
        {this.props.children}
      </Provider>
    )
  }
}
